import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Send, Image, Mic, X, Play, Pause, Check, CheckCheck, AtSign, MessageCircle, Volume2
} from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// Audio Player for voice notes
const VoicePlayer = ({ path, durationSec }) => {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('crewToken');
    if (!path) return;
    axios.get(`${API}/api/files/${path}`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    }).then(res => setBlobUrl(URL.createObjectURL(res.data))).catch(() => {});
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [path]);

  const toggle = () => {
    if (!audioRef.current) return;
    playing ? audioRef.current.pause() : audioRef.current.play();
    setPlaying(!playing);
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  return (
    <div className="flex items-center gap-2 bg-stone-100 rounded-full px-3 py-1.5 min-w-[140px]">
      {blobUrl && <audio ref={audioRef} src={blobUrl} onTimeUpdate={() => setProgress(audioRef.current?.currentTime || 0)} onEnded={() => { setPlaying(false); setProgress(0); }} />}
      <button onClick={toggle} className="text-amber-600">
        {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
      </button>
      <div className="flex-1 h-1 bg-stone-300 rounded-full overflow-hidden">
        <div className="h-full bg-amber-500 rounded-full" style={{ width: `${durationSec ? (progress / durationSec) * 100 : 0}%` }} />
      </div>
      <span className="text-[10px] text-stone-500">{playing ? fmt(progress) : fmt(durationSec || 0)}</span>
    </div>
  );
};

// Comment bubble with photo loading
const CommentBubble = ({ comment: c, myId, members, renderText }) => {
  const [photoUrl, setPhotoUrl] = useState(null);
  const isOwn = c.author_id === myId;
  const time = new Date(c.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const readCount = Object.keys(c.read_by || {}).length;

  useEffect(() => {
    if (c.message_type === 'photo' && c.attachment_path) {
      const tk = localStorage.getItem('token') || localStorage.getItem('crewToken');
      axios.get(`${API}/api/files/${c.attachment_path}`, {
        headers: { Authorization: `Bearer ${tk}` }, responseType: 'blob'
      }).then(r => setPhotoUrl(URL.createObjectURL(r.data))).catch(() => {});
    }
    return () => { if (photoUrl) URL.revokeObjectURL(photoUrl); };
  }, [c.attachment_path]);

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div className="max-w-[85%]">
        {!isOwn && <p className="text-[10px] text-stone-500 mb-0.5 font-medium">{c.author_name}</p>}
        <div className={`rounded-xl px-3 py-1.5 text-sm ${
          isOwn ? 'bg-amber-500 text-white rounded-tr-sm' : 'bg-white border border-stone-200 text-stone-800 rounded-tl-sm'
        }`}>
          {c.message_type === 'photo' && photoUrl && (
            <img src={photoUrl} alt="" className="rounded-lg max-w-[200px] max-h-[150px] object-cover mb-1" />
          )}
          {c.message_type === 'voice' && <VoicePlayer path={c.attachment_path} durationSec={c.attachment_duration} />}
          {c.text && <p className="whitespace-pre-wrap break-words">{renderText(c.text)}</p>}
        </div>
        <div className={`flex items-center gap-1 mt-0.5 ${isOwn ? 'justify-end' : ''} px-0.5`}>
          <span className="text-[9px] text-stone-400">{time}</span>
          {isOwn && (readCount > 1 ? <CheckCheck className="w-2.5 h-2.5 text-blue-500" /> : <Check className="w-2.5 h-2.5 text-stone-400" />)}
        </div>
      </div>
    </div>
  );
};

function TaskComments({ taskId, taskTitle }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [members, setMembers] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [showMention, setShowMention] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [sending, setSending] = useState(false);
  const [myId, setMyId] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recDuration, setRecDuration] = useState(0);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);

  const token = localStorage.getItem('token') || localStorage.getItem('crewToken');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setMyId(payload.type === 'crew' ? payload.sub : (payload.sub || payload.id));
    } catch { /* ignore */ }
  }, [token]);

  const fetchComments = useCallback(async () => {
    if (!taskId) return;
    try {
      const res = await axios.get(`${API}/api/crew/tasks/${taskId}/comments`, { headers });
      setComments(res.data || []);
    } catch { /* ignore */ }
  }, [taskId]);

  useEffect(() => {
    fetchComments();
    axios.get(`${API}/api/chat/members`, { headers }).then(r => setMembers(r.data || [])).catch(() => {});
  }, [taskId]);

  // Poll every 8s
  useEffect(() => {
    const interval = setInterval(fetchComments, 8000);
    return () => clearInterval(interval);
  }, [fetchComments]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [comments]);

  const handleTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    const cursor = e.target.selectionStart;
    const before = val.slice(0, cursor);
    const atMatch = before.match(/@(\w*)$/);
    if (atMatch) { setShowMention(true); setMentionFilter(atMatch[1]); }
    else setShowMention(false);
  };

  const handleMentionSelect = (member) => {
    const cursor = inputRef.current?.selectionStart || text.length;
    const before = text.slice(0, cursor);
    const after = text.slice(cursor);
    const atIdx = before.lastIndexOf('@');
    setText(before.slice(0, atIdx) + `@${member.name} ` + after);
    setShowMention(false);
    setMentions(prev => [...prev.filter(m => m.id !== member.id), { id: member.id, name: member.name }]);
    inputRef.current?.focus();
  };

  const sendComment = async (msgType = 'text', attachPath = null, attachName = null, attachDur = null) => {
    if (msgType === 'text' && !text.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/crew/tasks/${taskId}/comments`, {
        text: text.trim(), message_type: msgType,
        attachment_path: attachPath, attachment_name: attachName,
        attachment_duration: attachDur, mentions,
      }, { headers });
      setText('');
      setMentions([]);
      fetchComments();
    } catch { toast.error('Failed to send comment'); }
    setSending(false);
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/api/chat/upload`, fd, { headers });
      await sendComment('photo', res.data.path, file.name);
    } catch { toast.error('Upload failed'); }
    e.target.value = '';
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.start();
      setIsRecording(true);
      setRecDuration(0);
      timerRef.current = setInterval(() => setRecDuration(d => d + 1), 1000);
    } catch { toast.error('Microphone denied'); }
  };

  const sendVoice = async () => {
    return new Promise((resolve) => {
      if (!mediaRecRef.current) { resolve(); return; }
      mediaRecRef.current.onstop = async () => {
        clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        mediaRecRef.current.stream.getTracks().forEach(t => t.stop());
        setIsRecording(false);
        const fd = new FormData();
        fd.append('file', blob, 'voice.webm');
        try {
          const res = await axios.post(`${API}/api/chat/upload`, fd, { headers });
          await sendComment('voice', res.data.path, 'voice.webm', recDuration);
        } catch { toast.error('Voice upload failed'); }
        resolve();
      };
      mediaRecRef.current.stop();
    });
  };

  const cancelRec = () => {
    if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') {
      mediaRecRef.current.stream.getTracks().forEach(t => t.stop());
      mediaRecRef.current.stop();
    }
    clearInterval(timerRef.current);
    setIsRecording(false);
  };

  const renderText = (txt) => {
    if (!txt) return null;
    return txt.split(/(@\w[\w\s]*)/g).map((part, i) => {
      if (part.startsWith('@')) {
        const name = part.slice(1);
        if (members.some(m => m.name.toLowerCase() === name.toLowerCase())) {
          return <span key={i} className="text-amber-600 font-semibold bg-amber-50 rounded px-0.5">{part}</span>;
        }
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div className="flex flex-col h-full" data-testid="task-comments">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-stone-200 bg-stone-50">
        <MessageCircle className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-semibold text-stone-700">Comments</span>
        <span className="text-xs text-stone-400">({comments.length})</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2 max-h-[300px]">
        {comments.length === 0 && (
          <p className="text-xs text-stone-400 text-center py-4">No comments yet</p>
        )}
        {comments.map(c => (
          <CommentBubble key={c.id} comment={c} myId={myId} members={members} renderText={renderText} />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t border-stone-200 p-2">
        {isRecording ? (
          <div className="flex items-center gap-2 bg-red-50 rounded-lg px-3 py-2">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-xs text-red-600">{Math.floor(recDuration / 60)}:{String(recDuration % 60).padStart(2, '0')}</span>
            <div className="flex-1" />
            <button onClick={cancelRec} className="text-stone-400 hover:text-stone-600"><X className="w-4 h-4" /></button>
            <button onClick={sendVoice} className="bg-red-500 text-white rounded-full p-1"><Send className="w-3.5 h-3.5" /></button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <div className="relative flex-1">
              {showMention && (
                <div className="absolute bottom-full left-0 mb-1 bg-white border rounded-lg shadow-lg max-h-32 overflow-y-auto w-48 z-50">
                  {members.filter(m => m.name.toLowerCase().includes(mentionFilter.toLowerCase())).map(m => (
                    <button key={m.id} onClick={() => handleMentionSelect(m)} className="w-full text-left px-2 py-1.5 hover:bg-amber-50 text-xs flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full bg-amber-200 flex items-center justify-center text-[10px] font-bold text-amber-800">{m.name.charAt(0)}</div>
                      {m.name}
                    </button>
                  ))}
                </div>
              )}
              <input
                ref={inputRef}
                value={text}
                onChange={handleTextChange}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendComment(); } }}
                placeholder="Add a comment... @ to mention"
                className="w-full text-xs border border-stone-200 rounded-lg px-2.5 py-2 outline-none focus:border-amber-400"
                data-testid="task-comment-input"
              />
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
            <button onClick={() => fileInputRef.current?.click()} className="text-stone-400 hover:text-amber-600 p-1" data-testid="comment-photo-btn"><Image className="w-4 h-4" /></button>
            <button onClick={startRec} className="text-stone-400 hover:text-red-500 p-1" data-testid="comment-voice-btn"><Mic className="w-4 h-4" /></button>
            <button onClick={() => sendComment()} disabled={!text.trim() || sending} className="bg-amber-500 text-white rounded-lg p-1.5 disabled:opacity-40" data-testid="comment-send-btn"><Send className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>
    </div>
  );
}


export { TaskComments };
export default TaskComments;
